import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { BiPencil, BiTrash } from 'react-icons/bi';
import { PiDotsThree } from 'react-icons/pi';
import { z } from 'zod';

import { ButtonOutlinedSmall, ButtonSmall } from '@/components/Button';
import AddButton from '@/components/Button/AddButton';
import { Input } from '@/components/Inputs';
import { LoaderSpinner } from '@/components/LoaderSpinner/LoaderSpinner';
import { Popup, PopupSection, type PopupSectionItem } from '@/components/Popup/Popup';
import { Tile } from '@/components/Tile/Tile';
import { useClickOutside } from '@/hooks/useClickOutside';
import { describePosition } from '@/utils/position';

import { currentLocalTime, timeMaskProps } from '../../logbookTime';
import { useEntryFix } from '../../useEntryFix';
import { TIME_RE, type OilEntry } from '../types';

import s from './OilEntriesSection.module.scss';

interface OilEntriesSectionProps {
  entries: OilEntry[];
  editingId: string | null;
  addingNew: boolean;
  onStartAdd(): void;
  onStartEdit(id: string): void;
  onCancel(): void;
  onSaveEdit(id: string, patch: Partial<OilEntry>): void;
  onAddNew(values: Omit<OilEntry, 'id'>): void;
  onRemove(id: string): void;
}

export function OilEntriesSection({
  entries,
  editingId,
  addingNew,
  onStartAdd,
  onStartEdit,
  onCancel,
  onSaveEdit,
  onAddNew,
  onRemove,
}: OilEntriesSectionProps) {
  return (
    <Tile>
      <section className={s.section}>
        <h6 className={s.sectionTitle}>Bunker & Oil Entries</h6>

        {entries.length === 0 && !addingNew && (
          <p className={s.empty}>Please add an entry to get started.</p>
        )}

        {entries.map((entry, index) =>
          entry.id === editingId ? (
            <OilEntryEdit
              key={entry.id}
              number={index + 1}
              entry={entry}
              onCancel={onCancel}
              onSubmit={(values) => onSaveEdit(entry.id, values)}
            />
          ) : (
            <OilEntryItem
              key={entry.id}
              number={index + 1}
              entry={entry}
              onEdit={() => onStartEdit(entry.id)}
              onDelete={() => onRemove(entry.id)}
            />
          ),
        )}

        {addingNew ? (
          <OilEntryEdit
            number={entries.length + 1}
            onCancel={onCancel}
            onSubmit={(values) => onAddNew(values)}
          />
        ) : (
          <AddButton label="Bunker Entry" onClick={onStartAdd} />
        )}
      </section>
    </Tile>
  );
}

interface OilEntryItemProps {
  number: number;
  entry: OilEntry;
  onEdit(): void;
  onDelete(): void;
}

function OilEntryItem({ number, entry, onEdit, onDelete }: OilEntryItemProps) {
  const [opened, setOpened] = useState(false);
  const ref = useClickOutside<HTMLButtonElement>(() => setOpened(false));

  const actionsList: PopupSectionItem[] = [
    { label: 'Edit', onClick: onEdit, icon: <BiPencil /> },
    { label: 'Delete', onClick: onDelete, icon: <BiTrash /> },
  ];

  return (
    <article className={s.inputsGroup}>
      <div className={s.groupTitle}>
        <p>Entry #{number}</p>
        <button type="button" className={s.actions} onClick={() => setOpened((o) => !o)} ref={ref}>
          <PiDotsThree size={20} />
          {opened && (
            <Popup>
              <PopupSection items={actionsList} title="" onItemClick={() => setOpened(false)} />
            </Popup>
          )}
        </button>
      </div>
      <section className={s.inputs}>
        <Field label="Time" value={entry.time} />
        <Field label="Location" value={entry.location} />
        <Field label="Bunker Taken USG" value={entry.bunkerTakenUsg} />
        <Field label="Cost Per USG after Tax USD" value={entry.costPerUsgAfterTaxUsd} />
        <Field label="Cost USD" value={entry.costUsd} />
        <Field label="Cost Local Currency" value={entry.costLocalCurrency} />
        <Field label="Total USG Aboard" value={entry.totalOnboardUsg} />
      </section>
    </article>
  );
}

interface OilEntryEditProps {
  number: number;
  entry?: OilEntry;
  onCancel(): void;
  onSubmit(values: Omit<OilEntry, 'id'>): void;
}

const FIELD_MAX = 50;
const LOCATION_MAX = 120;

const fieldString = z.string().max(FIELD_MAX, `Max ${FIELD_MAX} characters`);

const numericField = z
  .string()
  .refine(
    (v) => v === '' || (!Number.isNaN(Number(v)) && Number(v) >= 0),
    'Please enter an amount of 0 or more.',
  );

const entrySchema = z.object({
  time: z.string().refine((v) => !v || TIME_RE.test(v), 'Use HH:MM format (00:00–23:59)'),
  location: z.string().max(LOCATION_MAX, `Max ${LOCATION_MAX} characters`),
  bunkerTakenUsg: numericField,
  costPerUsgAfterTaxUsd: numericField,
  costUsd: numericField,
  costLocalCurrency: fieldString,
  totalOnboardUsg: fieldString,
});

type EntryFormValues = z.infer<typeof entrySchema>;

function OilEntryEdit({ number, entry, onCancel, onSubmit }: OilEntryEditProps) {
  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<EntryFormValues>({
    resolver: zodResolver(entrySchema),
    mode: 'onChange',
    defaultValues: {
      time: entry?.time ?? currentLocalTime(),
      location: entry?.location ?? '',
      bunkerTakenUsg: entry?.bunkerTakenUsg ?? '',
      costPerUsgAfterTaxUsd: entry?.costPerUsgAfterTaxUsd ?? '',
      costUsd: entry?.costUsd ?? '',
      costLocalCurrency: entry?.costLocalCurrency ?? '',
      totalOnboardUsg: entry?.totalOnboardUsg ?? '',
    },
  });
  // A new entry starts where the boat is — bunkering happens in port, so as a place name, the way
  // the daily log names it.
  const locating = useEntryFix(!entry, { getValues, setValue }, async (coords) => {
    const { formattedAddress } = await describePosition(coords.latitude, coords.longitude);
    // ponytail: a long address is cut at the limit rather than failing Add on text nobody typed.
    return { location: formattedAddress.slice(0, LOCATION_MAX) };
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={s.inputsGroup}>
      <div className={s.groupTitle}>
        <p>Entry #{number}</p>
      </div>
      <section className={s.editInputs}>
        <Input
          label="Time"
          placeholder="HH:MM"
          error={errors.time?.message}
          {...timeMaskProps(register('time'))}
        />
        <Input
          label="Location"
          placeholder={locating ? 'Locating…' : 'IGY Marina Cabo San Lucas'}
          suffixNode={locating && <LoaderSpinner size={16} fullHeight={false} label="" />}
          error={errors.location?.message}
          {...register('location')}
        />
        <Input
          label="Bunker Taken USG"
          type="number"
          step="any"
          min={0}
          placeholder="e.g. 1935.65"
          error={errors.bunkerTakenUsg?.message}
          {...register('bunkerTakenUsg')}
        />
        <Input
          label="Cost Per USG after Tax USD"
          type="number"
          step="any"
          min={0}
          placeholder="e.g. 6.44"
          error={errors.costPerUsgAfterTaxUsd?.message}
          {...register('costPerUsgAfterTaxUsd')}
        />
        <Input
          label="Cost USD"
          type="number"
          step="any"
          min={0}
          placeholder="e.g. 12467.39"
          error={errors.costUsd?.message}
          {...register('costUsd')}
        />
        <Input
          label="Cost Local Currency"
          placeholder="250059.67 Peso"
          error={errors.costLocalCurrency?.message}
          {...register('costLocalCurrency')}
        />
        <Input
          label="Total USG Aboard"
          placeholder="2850"
          error={errors.totalOnboardUsg?.message}
          {...register('totalOnboardUsg')}
        />
      </section>
      <div className={s.groupItemActions}>
        <ButtonOutlinedSmall type="button" onClick={onCancel}>
          Cancel
        </ButtonOutlinedSmall>
        <ButtonSmall type="submit">{entry ? 'Save' : 'Add'}</ButtonSmall>
      </div>
    </form>
  );
}

interface FieldProps {
  label: string;
  value?: string;
}

function Field({ label, value }: FieldProps) {
  return (
    <article className={s.field}>
      <span className={`body-medium ${s.fieldLabel}`}>{label}</span>
      <div className={s.fieldValue}>{value && value.length ? value : '-'}</div>
    </article>
  );
}
